/**
 * ゲーム全体の組み立て。
 *
 * 3D（scene / player / interaction）とシナリオエンジン、UI、サウンドをここで繋ぐ。
 * 各部品は互いを直接知らず、この Game クラスだけが全体を知っている。
 */

import * as THREE from 'three';
import type { AdminScreen } from '../types/admin';
import type { DeviceId, RoomId } from '../types/network';
import type { Chapter, Fix, Interactable } from '../types/scenario';
import { AudioManager } from '../audio/AudioManager';
import { evaluateCondition } from '../scenario/conditions';
import { ScenarioEngine } from '../scenario/engine';
import { DialogueRunner, type DialogueView } from '../scenario/dialogueRunner';
import { loadGameData, type GlossaryEntry } from '../scenario/dataSource';
import { clearSave, hasSave } from '../scenario/state';
import { bindWorldFlags } from '../sim/wireless';
import { ipconfig } from '../sim/diagnostics';
import { AdminScreenView, findAdminScreen, loadAdminScreens } from '../ui/admin';
import { DialogueWindow } from '../ui/dialogue';
import { Hud, shouldShowResumeOverlay } from '../ui/hud';
import { InspectPanel } from '../ui/inspect';
import { ChapterClearScreen, SettingsMenu, TitleScreen } from '../ui/menus';
import { TrafficMonitor } from '../ui/monitor';
import { Notebook } from '../ui/notebook';
import { WirelessScanner } from '../ui/scanner';
import { TerminalScreen } from '../ui/terminal';
import { TermPopup, type TermSource } from '../ui/terms';
import { TouchControls, usesTouchControls } from '../ui/touchControls';
import {
  buildOfficeLayout,
  formatPlaceLabel,
  type OfficeLayout,
  type PlayerPlace,
} from './layout';
import { InteractionRaycaster, PointerLockManager } from './interaction';
import { buildCollisionObstacles } from './obstacles';
import { FirstPersonController } from './player';
import { OfficeScene } from './scene';
import { loadSettings, persistSettings, type GameSettings } from './settings';

type Mode = 'title' | 'playing';

/**
 * 調査パネルに出す「道具を開く」ボタンの ID。
 *
 * InspectPanel は Fix のボタンを並べる作りなので、道具を開く導線も
 * 同じ形で差し込む。engine の章データには存在しない ID を使い、
 * applyFix() の入口で横取りする（章データを汚さない）。
 */
const OPEN_ADMIN_FIX_ID = '__open_admin__';
const OPEN_SCANNER_FIX_ID = '__open_scanner__';
const OPEN_MONITOR_FIX_ID = '__open_monitor__';
const OPEN_TERMINAL_FIX_ID = '__open_terminal__';

/**
 * トラフィックモニターの入口になる機器の種類。
 * 通信が集まる場所（スイッチ・ルーター・ファイアウォール）から覗く道具にする。
 */
const MONITOR_DEVICE_KINDS = ['switch', 'router', 'firewall'] as const;

export class Game {
  private readonly scene: OfficeScene;
  private readonly engine: ScenarioEngine;
  private readonly audio = new AudioManager();
  private readonly pointerLock: PointerLockManager;
  private readonly raycaster: InteractionRaycaster;
  private player: FirstPersonController;
  private layout: OfficeLayout;

  private readonly hud: Hud;
  private readonly touchMode = usesTouchControls();
  private readonly touchControls: TouchControls;
  private readonly dialogueWindow: DialogueWindow;
  private readonly dialogueRunner: DialogueRunner;
  private readonly notebook: Notebook;
  private readonly terminal: TerminalScreen;
  private readonly inspect: InspectPanel;
  private readonly admin: AdminScreenView;
  private readonly scanner: WirelessScanner;
  private readonly monitor: TrafficMonitor;
  private readonly termPopup: TermPopup;
  private readonly title: TitleScreen;
  private readonly settingsMenu: SettingsMenu;
  private readonly clearScreen: ChapterClearScreen;

  private settings: GameSettings;
  private mode: Mode = 'title';
  private clock = new THREE.Clock();
  private currentTargetId: string | null = null;
  /** いまオーバーレイを出しているか。毎フレーム DOM を触らないための前回値 */
  private resumeOverlayVisible = false;
  private pendingClearChapter: Chapter | null = null;
  /** いま調査パネルに出している機器（道具を開くボタンの対象） */
  private inspectDeviceId: DeviceId | null = null;
  private pendingPcInspection: { interactable: Interactable; description: string; terms?: string[] } | null = null;
  private readonly adminScreens: AdminScreen[] = loadAdminScreens();
  private running = false;
  private disposers: (() => void)[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    uiRoot: HTMLElement,
  ) {
    this.settings = loadSettings();
    document.documentElement.classList.toggle('touch-mode', this.touchMode);

    const data = loadGameData();
    this.engine = new ScenarioEngine(data);
    this.layout = buildOfficeLayout(data.world);

    this.scene = new OfficeScene(canvas);
    this.scene.setFov(this.settings.fov);
    this.scene.buildOffice(this.layout);
    this.scene.buildInteractables(this.layout, this.engine.interactables, this.engine.world);

    this.raycaster = new InteractionRaycaster(this.scene.camera);
    this.pointerLock = new PointerLockManager(canvas);
    this.player = new FirstPersonController(this.scene.camera, this.layout, this.settings, {
      onFootstep: (material, onStairs) => {
        this.audio.play(
          onStairs ? 'footstep_stair' : material === 'carpet' ? 'footstep_carpet' : 'footstep_tile',
          { throttleMs: 180 },
        );
      },
      onRoomChange: (roomId, place) => this.handleRoomChange(roomId, place),
      onBlocked: (roomId) => {
        const room = this.engine.world.rooms[roomId];
        this.hud.toast('鍵がかかっている', `${room?.name ?? roomId}には、まだ入れない。`, 'info');
        this.audio.play('cancel');
      },
    }, buildCollisionObstacles(this.layout, this.engine.interactables));

    // --- UI -----------------------------------------------------
    const termSource: TermSource = {
      lookup: (term) => this.engine.getGlossaryEntry(term),
      card: (cardId) => this.engine.getCard(cardId),
      hasCard: (cardId) => this.engine.state.knowledgeCards.includes(cardId),
      markSeen: (term) => this.engine.markTermSeen(term),
    };
    const openTerm = (entry: GlossaryEntry | null, term: string): void => {
      this.audio.play('examine');
      this.termPopup.open(entry, term);
    };

    this.hud = new Hud(() => this.requestPointerLock());
    this.touchControls = new TouchControls({
      onMove: (x, z) => this.player.setTouchMove(x, z),
      onPitch: direction => this.player.setTouchPitch(direction),
      onAction: action => {
        this.audio.unlock();
        if (!this.canControlPlayer()) return;
        switch (action) {
          case 'interact': this.tryInteract(); break;
          case 'notebook': this.openNotebook(); break;
          case 'terminal': this.openTerminal(); break;
          case 'scanner': this.openScanner(null); break;
          case 'monitor': this.openMonitor(); break;
          case 'menu': this.handleEscape(); break;
        }
      },
    });
    this.termPopup = new TermPopup(termSource);
    this.termPopup.setCardOpener((cardId) => this.notebook.openCard(cardId));

    this.dialogueWindow = new DialogueWindow(
      termSource,
      openTerm,
      () => this.dialogueRunner.advance(),
      (index) => {
        this.audio.play('confirm');
        this.dialogueRunner.choose(index);
      },
      () => this.audio.play('terminal_type', { throttleMs: 45 }),
    );
    this.dialogueRunner = new DialogueRunner(this.engine, (view) => this.onDialogueUpdate(view));

    this.notebook = new Notebook(this.engine, {
      onChooseHypothesis: (id) => this.chooseHypothesis(id),
      onRequestHint: () => {
        this.audio.play('notify');
        this.engine.revealNextHint();
      },
      onClose: () => this.closeNotebook(),
      onTermClick: openTerm,
    });
    this.notebook.setLayout(this.layout);

    this.terminal = new TerminalScreen({
      onCommandExecuted: (command, argument, success) => {
        this.audio.play('terminal_done');
        this.handleCommandExecuted(command, argument, success);
      },
      onType: () => this.audio.play('terminal_type', { throttleMs: 35 }),
      onClose: () => this.closeTerminal(),
    });

    this.inspect = new InspectPanel(termSource, {
      onClose: () => this.closeInspect(),
      onApplyFix: (fixId) => this.applyFix(fixId),
      onTermClick: openTerm,
    });

    // 管理画面。UI は world を直接書き換えず、必ず Effect を engine に渡す
    this.admin = new AdminScreenView(termSource, {
      onEffects: (effects) => {
        this.engine.runEffects(effects);
        this.audio.play('confirm');
        this.afterWorldChange();
      },
      onClose: () => this.closeAdmin(),
      onTermClick: openTerm,
      evaluate: (condition) =>
        evaluateCondition(condition, this.engine.state, this.engine.world),
      // confirm は渡さない。管理画面が自前の確認ウィンドウ（ゲーム内 UI）を出す。
      // ブラウザ既定の confirm は埋め込み環境で抑止され、操作が無言で失敗する
    });

    this.scanner = new WirelessScanner({ onClose: () => this.closeScanner() });
    this.monitor = new TrafficMonitor({ onClose: () => this.closeMonitor() });

    this.title = new TitleScreen({
      onStart: (name) => this.startNewGame(name),
      onContinue: () => this.continueGame(),
      onOpenSettings: () => this.settingsMenu.open(false),
      hasSave: () => hasSave(),
    });

    this.settingsMenu = new SettingsMenu(this.settings, {
      onChange: (settings) => this.applySettings(settings),
      onClose: () => this.closeSettings(),
      onSave: () => {
        if (this.engine.save()) {
          this.hud.toast('セーブしました', '進行状況を保存しました。', 'info');
        }
      },
      onReturnToTitle: () => this.returnToTitle(),
    });

    this.clearScreen = new ChapterClearScreen({ onNext: () => this.advanceAfterClear() });

    uiRoot.append(
      this.hud.root,
      this.touchControls.root,
      this.dialogueWindow.root,
      this.inspect.root,
      this.admin.root,
      this.scanner.root,
      this.monitor.root,
      this.notebook.root,
      this.terminal.root,
      this.termPopup.root,
      this.clearScreen.root,
      this.settingsMenu.root,
      this.title.root,
    );

    this.hud.setVisible(false);
    this.title.show();
    this.bindEvents();
    this.bindEngineEvents();
    this.applySettings(this.settings, false);
  }

  // ------------------------------------------------------------
  // ループ
  // ------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.clock.start();
    const loop = (): void => {
      if (!this.running) return;
      requestAnimationFrame(loop);
      this.update();
    };
    requestAnimationFrame(loop);
  }

  private update(): void {
    const delta = this.clock.getDelta();
    this.syncPendingPcInspection();

    // player.setEnabled と同じく、オーバーレイの表示も毎フレーム現在の状態から決め直す。
    // イベント任せにすると、状態が変化しないまま Pointer Lock に失敗した場合に
    // 通知が飛ばず「動けないのに案内も出ない」詰み状態になる。
    this.syncResumeOverlay();
    this.touchControls.setEnabled(this.touchMode && this.canControlPlayer());

    if (this.mode === 'playing') {
      this.player.setEnabled(this.canControlPlayer());
      this.player.update(delta);
      this.engine.tick(delta);
      this.updateTargeting();
    }
    this.scene.render();
  }

  /**
   * 「クリックで再開」オーバーレイの表示を現在の状態に合わせる。
   * setResumeOverlay を呼ぶのはここだけにして、表示が状態と食い違わないようにする。
   */
  private syncResumeOverlay(): void {
    const visible = shouldShowResumeOverlay({
      playing: this.mode === 'playing',
      anyModalOpen: this.isAnyModalOpen(),
      pointerLocked: this.touchMode || this.pointerLock.isLocked,
    });
    if (visible === this.resumeOverlayVisible) return;
    this.resumeOverlayVisible = visible;
    this.hud.setResumeOverlay(visible);
  }

  private updateTargeting(): void {
    if (!this.canControlPlayer()) {
      if (this.currentTargetId !== null) {
        this.currentTargetId = null;
        this.scene.setHighlighted(null);
        this.hud.setTarget(null);
      }
      return;
    }
    const target = this.raycaster.pick(this.scene.handles);
    const id = target?.handle.id ?? null;
    if (id === this.currentTargetId) return;

    this.currentTargetId = id;
    this.scene.setHighlighted(id);
    if (!target) {
      this.hud.setTarget(null);
      return;
    }
    const interactable = target.handle.interactable;
    const hint = this.touchMode ? '右下をタップ' : interactable.visual === 'person' ? '[E] 話す' : '[E] 調べる';
    this.hud.setTarget(interactable.label, hint);
  }

  // ------------------------------------------------------------
  // 入力
  // ------------------------------------------------------------

  private bindEvents(): void {
    const onResize = (): void => {
      const viewport = window.visualViewport;
      if (!viewport || viewport.scale === 1) {
        document.documentElement.style.setProperty('--app-height', `${viewport?.height ?? window.innerHeight}px`);
      }
      this.touchControls.reset();
      this.player.releaseAllKeys();
      this.scene.resize();
    };
    const onKeyDown = (event: KeyboardEvent): void => this.handleKeyDown(event);
    const onKeyUp = (event: KeyboardEvent): void => this.player.handleKeyUp(event);
    const onBlur = (): void => { this.touchControls.reset(); this.player.releaseAllKeys(); };
    const onVisibility = (): void => { if (document.hidden) onBlur(); };
    const onMouseMove = (event: MouseEvent): void => {
      if (this.pointerLock.isLocked && this.canControlPlayer()) {
        this.player.handleMouseMove(event.movementX, event.movementY);
      }
    };
    const onCanvasClick = (): void => {
      this.audio.unlock();
      if (this.mode !== 'playing') return;
      if (this.touchMode) return;
      if (!this.pointerLock.isLocked) {
        this.requestPointerLock();
        return;
      }
      this.tryInteract();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVisibility);
    window.visualViewport?.addEventListener('resize', onResize);
    document.addEventListener('mousemove', onMouseMove);
    this.canvas.addEventListener('click', onCanvasClick);
    onResize();

    // オーバーレイの表示は update() が毎フレーム決め直す。
    // ここ（状態遷移イベント）では表示に触らない。
    // 状態が変わらないまま再要求が失敗すると、このリスナーは呼ばれないため。
    const unsubscribe = this.pointerLock.onChange((state) => {
      if (state !== 'locked') this.player.releaseAllKeys();
    });

    this.disposers.push(
      () => window.removeEventListener('resize', onResize),
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('blur', onBlur),
      () => document.removeEventListener('visibilitychange', onVisibility),
      () => window.visualViewport?.removeEventListener('resize', onResize),
      () => document.removeEventListener('mousemove', onMouseMove),
      () => this.canvas.removeEventListener('click', onCanvasClick),
      unsubscribe,
    );
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // 端末の入力欄など、フォーム要素に入力中はゲーム操作を横取りしない
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
      if (event.key !== 'Escape') return;
    }
    this.audio.unlock();

    if (this.mode === 'title') {
      if (event.key === 'Escape' && this.settingsMenu.isOpen) {
        event.preventDefault();
        this.closeSettings();
      }
      return;
    }

    switch (event.code) {
      case 'Escape':
        event.preventDefault();
        this.handleEscape();
        return;
      case 'Tab':
        event.preventDefault();
        if (this.isBlockingModalOpen()) return;
        if (this.notebook.isOpen) this.closeNotebook();
        else this.openNotebook();
        return;
      case 'KeyT':
        if (this.dialogueWindow.isOpen || this.inspect.isOpen || this.notebook.isOpen) return;
        if (this.settingsMenu.isOpen || this.clearScreen.isOpen) return;
        event.preventDefault();
        if (this.terminal.isOpen) this.closeTerminal();
        else this.openTerminal();
        return;
      case 'KeyR':
        // 電波スキャナー。端末（T）と同じく、どこからでも開ける道具として扱う
        if (this.dialogueWindow.isOpen || this.inspect.isOpen || this.notebook.isOpen) return;
        if (this.settingsMenu.isOpen || this.clearScreen.isOpen || this.admin.isOpen) return;
        event.preventDefault();
        if (this.scanner.isOpen) this.closeScanner();
        else this.openScanner(null);
        return;
      case 'KeyM':
        // トラフィックモニター。電波スキャナー（R）と同じ扱いの計測器
        if (this.dialogueWindow.isOpen || this.inspect.isOpen || this.notebook.isOpen) return;
        if (this.settingsMenu.isOpen || this.clearScreen.isOpen || this.admin.isOpen) return;
        event.preventDefault();
        if (this.monitor.isOpen) this.closeMonitor();
        else this.openMonitor();
        return;
      case 'KeyE':
      case 'Space':
        if (this.dialogueWindow.isOpen) {
          event.preventDefault();
          this.dialogueWindow.handleAdvance();
          return;
        }
        if (this.inspect.isOpen) {
          event.preventDefault();
          this.closeInspect();
          return;
        }
        if (event.code === 'KeyE' && !this.isAnyModalOpen()) {
          event.preventDefault();
          this.tryInteract();
          return;
        }
        break;
      default:
        break;
    }

    if (this.canControlPlayer() && event.code.startsWith('Arrow')) event.preventDefault();
    this.player.handleKeyDown(event);
  }

  private handleEscape(): void {
    if (this.termPopup.isOpen) {
      this.termPopup.close();
      this.audio.play('cancel');
      return;
    }
    if (this.admin.isOpen) {
      // 確認ウィンドウが出ていれば、まずそれを取り消す（画面はまだ閉じない）
      if (this.admin.dismissConfirm()) {
        this.audio.play('cancel');
        return;
      }
      this.closeAdmin();
      return;
    }
    if (this.scanner.isOpen) {
      this.closeScanner();
      return;
    }
    if (this.monitor.isOpen) {
      this.closeMonitor();
      return;
    }
    if (this.inspect.isOpen) {
      this.closeInspect();
      return;
    }
    if (this.terminal.isOpen) {
      this.closeTerminal();
      return;
    }
    if (this.notebook.isOpen) {
      this.closeNotebook();
      return;
    }
    if (this.clearScreen.isOpen) return;
    if (this.settingsMenu.isOpen) {
      this.closeSettings();
      return;
    }
    this.audio.play('notebook_open');
    this.pointerLock.release();
    this.settingsMenu.setSettings(this.settings);
    this.settingsMenu.open(true);
  }

  // ------------------------------------------------------------
  // モーダルの開閉
  // ------------------------------------------------------------

  /** プレイヤーが移動・視点操作できる状態か */
  private canControlPlayer(): boolean {
    return this.mode === 'playing' && !document.hidden && !this.isAnyModalOpen() && (this.touchMode || this.pointerLock.isLocked);
  }

  private isAnyModalOpen(): boolean {
    return (
      this.dialogueWindow.isOpen ||
      this.notebook.isOpen ||
      this.terminal.isOpen ||
      this.inspect.isOpen ||
      this.admin.isOpen ||
      this.scanner.isOpen ||
      this.monitor.isOpen ||
      this.settingsMenu.isOpen ||
      this.clearScreen.isOpen ||
      this.termPopup.isOpen ||
      this.title.isOpen
    );
  }

  /** Tab / T を無効にすべき状態か */
  private isBlockingModalOpen(): boolean {
    return (
      this.dialogueWindow.isOpen ||
      this.inspect.isOpen ||
      this.terminal.isOpen ||
      this.admin.isOpen ||
      this.scanner.isOpen ||
      this.monitor.isOpen ||
      this.settingsMenu.isOpen ||
      this.clearScreen.isOpen
    );
  }

  private requestPointerLock(): void {
    if (this.mode !== 'playing' || this.isAnyModalOpen()) return;
    this.audio.unlock();
    this.audio.startAmbience();
    if (!this.touchMode) this.pointerLock.request();
    // 表示の更新は syncResumeOverlay() に任せる。
    // ここで消してしまうと、要求が失敗した時に案内が出ないまま残る。
  }

  private openNotebook(): void {
    this.audio.play('notebook_open');
    this.pointerLock.release();
    this.notebook.setPlayerRoom(this.player.roomId);
    this.notebook.open();
  }

  private closeNotebook(): void {
    this.audio.play('notebook_close');
    this.notebook.close();
    this.requestPointerLock();
  }

  private openTerminal(deviceId: DeviceId = this.engine.playerDeviceId): void {
    if (this.engine.world.devices[deviceId]?.kind !== 'pc') return;
    this.audio.play('notebook_open');
    this.inspect.close();
    this.pointerLock.release();
    this.bindSimFlags();
    this.terminal.attachWorld(this.engine.world, deviceId);
    this.terminal.open();
  }

  private closeTerminal(): void {
    this.audio.play('cancel');
    this.terminal.close();
    this.requestPointerLock();
  }

  /**
   * 進行フラグを world に結び付ける。
   *
   * 干渉源が動いているか（電子レンジが回っているか等）は GameState.flags が持つ。
   * ping / traceroute は sim の関数なので GameState を知らない。
   * ここで参照を渡しておくと、sim 側が同じフラグを見られる。
   * 参照渡しなので、以後 applyEffect がフラグを書き換えれば sim 側にもそのまま届く。
   * engine.load() / reset() は state を作り直すので、その後に必ず呼び直す。
   */
  private bindSimFlags(): void {
    bindWorldFlags(this.engine.world, this.engine.state.flags);
  }

  /** その機器の管理画面（あれば） */
  private adminScreenFor(deviceId: DeviceId | null): AdminScreen | null {
    if (!deviceId) return null;
    const screen = findAdminScreen(this.adminScreens, this.engine.world, deviceId);
    if (!screen) return null;
    if (!evaluateCondition(screen.openCondition, this.engine.state, this.engine.world)) return null;
    return screen;
  }

  private openAdmin(deviceId: DeviceId): void {
    const screen = this.adminScreenFor(deviceId);
    if (!screen) return;
    this.audio.play('notebook_open');
    this.inspect.close();
    this.pointerLock.release();
    this.bindSimFlags();
    this.admin.open(screen, this.engine.world, deviceId);
  }

  private closeAdmin(): void {
    this.audio.play('cancel');
    this.admin.close();
    this.requestPointerLock();
  }

  /**
   * 電波スキャナーを開く。
   * 機器を指定しない場合は、いま居る部屋の AP を優先して選ぶ。
   */
  private openScanner(deviceId: DeviceId | null): void {
    const world = this.engine.world;
    const candidates = WirelessScanner.accessPointIds(world);
    if (candidates.length === 0) {
      this.hud.toast(
        '電波スキャナー',
        'まだ無線アクセスポイントを見つけていない。',
        'info',
      );
      return;
    }
    const inRoom = candidates.find((id) => world.devices[id].roomId === this.player.roomId);
    const target = deviceId && candidates.includes(deviceId) ? deviceId : inRoom ?? candidates[0];
    this.audio.play('notebook_open');
    this.inspect.close();
    this.pointerLock.release();
    this.bindSimFlags();
    this.scanner.open(world, target, this.engine.state.flags);
  }

  private closeScanner(): void {
    this.audio.play('cancel');
    this.scanner.close();
    this.requestPointerLock();
  }

  /**
   * トラフィックモニターを開く。
   * スキャナーと違って測る対象を選ばない。混雑は「どのリンクか」の話であって
   * 「どの機器か」の話ではない、というのが章 6 の見方そのものなので、
   * 常にネットワーク全体を出す。
   */
  private openMonitor(): void {
    this.audio.play('notebook_open');
    this.inspect.close();
    this.pointerLock.release();
    this.bindSimFlags();
    this.monitor.open(this.engine.world);
  }

  private closeMonitor(): void {
    this.audio.play('cancel');
    this.monitor.close();
    this.requestPointerLock();
  }

  private closeInspect(): void {
    this.inspect.close();
    this.audio.play('cancel');
    this.requestPointerLock();
  }

  private closeSettings(): void {
    this.settingsMenu.close();
    persistSettings(this.settings);
    this.audio.play('cancel');
    if (this.mode === 'playing') this.requestPointerLock();
  }

  // ------------------------------------------------------------
  // ゲーム進行
  // ------------------------------------------------------------

  private startNewGame(playerName: string): void {
    clearSave();
    this.engine.reset(playerName);
    this.enterPlaying();
    const chapter = this.engine.chapters[0];
    if (!chapter) return;
    const briefing = this.engine.startChapter(chapter.id);
    if (briefing) this.dialogueRunner.start(briefing.id);
  }

  private continueGame(): void {
    if (!this.engine.load()) {
      this.hud.toast('セーブがありません', 'はじめから開始してください。', 'info');
      return;
    }
    this.enterPlaying();
    this.engine.resumeChapter();
    this.refreshWorldVisuals();
  }

  private enterPlaying(): void {
    this.pendingPcInspection = null;
    this.mode = 'playing';
    this.bindSimFlags();
    this.title.hide();
    this.hud.setVisible(true);
    this.audio.unlock();
    this.audio.startAmbience();
    this.player.setLayout(this.layout);
    this.refreshHud();
    this.refreshLockedRooms();
    this.requestPointerLock();
  }

  private returnToTitle(): void {
    this.pendingPcInspection = null;
    this.engine.save();
    this.mode = 'title';
    this.settingsMenu.close();
    this.dialogueRunner.cancel();
    this.notebook.close();
    this.terminal.close();
    this.inspect.close();
    this.admin.close();
    this.scanner.close();
    this.monitor.close();
    this.clearScreen.hide();
    this.pointerLock.release();
    this.hud.setVisible(false);
    this.title.show();
    this.syncResumeOverlay();
  }

  private tryInteract(): void {
    if (!this.canControlPlayer()) return;
    const target = this.raycaster.pick(this.scene.handles);
    if (!target) return;

    this.audio.play('examine');
    const interactable = target.handle.interactable;
    this.scene.faceCharacter(interactable.id);
    const { outcome } = this.engine.interact(interactable.id);
    this.inspectDeviceId = interactable.deviceId ?? null;
    const fixes = [
      ...this.toolButtonsFor(interactable),
      ...this.engine.getFixesForInteractable(interactable.id),
    ];

    const network = this.inspectedPcConfig();
    // PCを調べて会話が始まる場合は、会話が終わってから設定を見せる。
    if (network && this.dialogueWindow.isOpen) {
      this.pendingPcInspection = { interactable, description: outcome?.description ?? '', terms: outcome?.terms };
      this.afterWorldChange();
      return;
    }

    // 説明文も対処も無い場合は、何も起きなかったことだけ伝える
    if (!outcome?.description && fixes.length === 0 && !network) {
      this.afterWorldChange();
      return;
    }
    if (outcome?.description || fixes.length > 0 || network) {
      this.pointerLock.release();
      this.inspect.show(
        interactable.label,
        outcome?.description ?? '',
        outcome?.terms,
        fixes,
        network,
      );
    }
    this.afterWorldChange();
  }

  private inspectedPcConfig() {
    const id = this.inspectDeviceId;
    return id && this.engine.world.devices[id]?.kind === 'pc'
      ? ipconfig(this.engine.world, id)
      : null;
  }

  private syncPendingPcInspection(): void {
    if (!this.pendingPcInspection || this.mode !== 'playing' || this.isAnyModalOpen()) return;
    const { interactable, description, terms } = this.pendingPcInspection;
    this.pendingPcInspection = null;
    this.inspectDeviceId = interactable.deviceId ?? null;
    this.pointerLock.release();
    this.inspect.show(interactable.label, description, terms, [
      ...this.toolButtonsFor(interactable),
      ...this.engine.getFixesForInteractable(interactable.id),
    ], this.inspectedPcConfig());
  }

  /**
   * 調査パネルに出す「道具を開く」ボタン。
   *
   * その機器に対応する管理画面があれば「管理画面を開く」、
   * 無線設定を持つ機器なら「電波スキャナーで見る」を出す。
   * PCならそのPCの端末も開ける。applyFix() が横取りして画面を開く。
   */
  private toolButtonsFor(interactable: Interactable): Fix[] {
    const deviceId = interactable.deviceId;
    if (!deviceId || !this.engine.world.devices[deviceId]) return [];
    const buttons: Fix[] = [];
    if (this.engine.world.devices[deviceId].kind === 'pc') {
      buttons.push({ id: OPEN_TERMINAL_FIX_ID, label: 'このPCの端末を開く', effects: [] });
    }
    if (this.adminScreenFor(deviceId)) {
      buttons.push({ id: OPEN_ADMIN_FIX_ID, label: '管理画面を開く', effects: [] });
    }
    if (this.engine.world.devices[deviceId].wireless) {
      buttons.push({ id: OPEN_SCANNER_FIX_ID, label: '電波スキャナーで見る', effects: [] });
    }
    const kind = this.engine.world.devices[deviceId].kind;
    if ((MONITOR_DEVICE_KINDS as readonly string[]).includes(kind)) {
      buttons.push({ id: OPEN_MONITOR_FIX_ID, label: 'トラフィックモニターで見る', effects: [] });
    }
    return buttons;
  }

  private applyFix(fixId: string): void {
    // 道具を開くボタンは章データの対処ではないので、engine へ渡す前に横取りする
    if (fixId === OPEN_TERMINAL_FIX_ID) {
      if (this.inspectDeviceId) this.openTerminal(this.inspectDeviceId);
      return;
    }
    if (fixId === OPEN_ADMIN_FIX_ID) {
      if (this.inspectDeviceId) this.openAdmin(this.inspectDeviceId);
      return;
    }
    if (fixId === OPEN_SCANNER_FIX_ID) {
      this.openScanner(this.inspectDeviceId);
      return;
    }
    if (fixId === OPEN_MONITOR_FIX_ID) {
      this.openMonitor();
      return;
    }

    const { fix, applied } = this.engine.applyFix(fixId);
    // 提示されていない対処（順序を飛ばした呼び出し）は engine 側で弾かれる。
    // UI には提示されないので通常は起きないが、起きても何も表示しない。
    if (!fix || !applied) return;
    const interactableId = fix.requiresInteractable ?? this.currentTargetId;
    const label = interactableId
      ? this.engine.getInteractable(interactableId)?.label ?? '対処'
      : '対処';
    const interactable = interactableId ? this.engine.getInteractable(interactableId) : null;
    const remaining = [
      ...(interactable ? this.toolButtonsFor(interactable) : []),
      ...(interactableId ? this.engine.getFixesForInteractable(interactableId) : []),
    ];
    this.inspect.show(label, fix.resultText ?? '対処を実行した。', undefined, remaining, this.inspectedPcConfig());
    this.afterWorldChange();
  }

  private chooseHypothesis(hypothesisId: string): void {
    const { hypothesis, dialogue } = this.engine.chooseHypothesis(hypothesisId);
    if (!hypothesis) return;
    this.audio.play(hypothesis.correct ? 'confirm' : 'error');
    this.notebook.close();
    this.requestPointerLock();
    if (dialogue) this.dialogueRunner.start(dialogue.id);
    this.refreshHud();
  }

  private handleCommandExecuted(command: string, argument: string, success: boolean): void {
    this.engine.state.flags[`used_cmd_${command}`] = true;
    if (command === 'ping') {
      this.engine.state.flags['tutorial_ping_done'] = true;
    }
    void argument;
    void success;
    this.refreshHud();
    this.checkClear();
  }

  private onDialogueUpdate(view: DialogueView | null): void {
    this.scene.setDialogueSpeaker(view?.line.speaker ?? null, view ? `${view.dialogueId}:${view.index}` : '');
    this.dialogueWindow.render(view);
    if (view === null) {
      this.refreshHud();
      if (this.pendingClearChapter) {
        const chapter = this.pendingClearChapter;
        this.pendingClearChapter = null;
        this.showChapterClear(chapter);
        return;
      }
      this.requestPointerLock();
      this.checkClear();
    } else {
      this.pointerLock.release();
    }
  }

  private showChapterClear(chapter: Chapter): void {
    this.pendingPcInspection = null;
    this.audio.play('chapter_clear');
    this.pointerLock.release();
    const cards = chapter.knowledgeCards
      .map((id) => this.engine.getCard(id))
      .filter((card): card is NonNullable<typeof card> => !!card);
    this.clearScreen.show(
      chapter,
      cards,
      this.engine.state.chapterStats[chapter.id],
      this.engine.nextChapter() !== null,
    );
    this.engine.save();
  }

  private advanceAfterClear(): void {
    this.clearScreen.hide();
    const next = this.engine.nextChapter();
    if (!next) {
      this.returnToTitle();
      return;
    }
    const briefing = this.engine.startChapter(next.id);
    this.refreshWorldVisuals();
    this.refreshHud();
    this.requestPointerLock();
    if (briefing) this.dialogueRunner.start(briefing.id);
  }

  private checkClear(): void {
    const chapter = this.engine.checkChapterClear();
    if (!chapter) return;
    this.pendingClearChapter = chapter;
    const debrief = this.engine.getDebriefDialogue();
    if (debrief) {
      this.dialogueRunner.start(debrief.id);
    } else {
      this.pendingClearChapter = null;
      this.showChapterClear(chapter);
    }
  }

  private afterWorldChange(): void {
    this.refreshWorldVisuals();
    this.refreshHud();
    this.refreshLockedRooms();
    // 開いている道具は world から読み直す（表示が世界と食い違わないようにする）
    if (this.scanner.isOpen) this.scanner.render();
    if (this.monitor.isOpen) this.monitor.render();
    if (this.admin.isOpen) this.admin.render();
    this.checkClear();
  }

  private refreshWorldVisuals(): void {
    this.scene.refreshLinkLeds(this.engine.world);
    if (this.inspect.isOpen) this.inspect.setNetwork(this.inspectedPcConfig());
  }

  private refreshLockedRooms(): void {
    const locked: RoomId[] = [];
    for (const id of Object.keys(this.engine.world.rooms)) {
      const room = this.engine.world.rooms[id];
      if (room.lockedUntilFlag && this.engine.state.flags[room.lockedUntilFlag] !== true) {
        locked.push(id);
      }
    }
    this.player.setLockedRooms(locked);
  }

  private refreshHud(): void {
    const chapter = this.engine.chapter;
    if (chapter) {
      this.hud.setObjectives(
        `第${chapter.number}章\u3000${chapter.title}`,
        this.engine
          .getObjectives()
          .filter((o) => o.visible)
          .map((o) => ({ text: o.objective.text, done: o.done })),
      );
    }
    const hint = this.engine.getHintState();
    this.hud.setHintPrompt(
      hint.available && hint.offered && hint.revealed === 0
        ? `ポート：「行き詰まりましたか？ 手帳${this.touchMode ? '' : '（Tab）'}からヒントを出せます。」`
        : null,
    );
    if (this.notebook.isOpen) this.notebook.render();
  }

  private handleRoomChange(roomId: RoomId | null, place: PlayerPlace | null): void {
    const room = roomId ? this.engine.world.rooms[roomId] : null;
    // 廊下・階段では部屋名が無いので、階数だけのラベル（「3F 廊下」）を出す
    this.hud.setRoom(formatPlaceLabel(place, room?.name ?? null));
    this.notebook.setPlayerRoom(roomId);
  }

  // ------------------------------------------------------------
  // エンジンのイベント
  // ------------------------------------------------------------

  private bindEngineEvents(): void {
    this.engine.events.on('dialogue', (dialogueId) => this.dialogueRunner.start(dialogueId));
    this.engine.events.on('sound', (soundId) => this.audio.playById(soundId));
    this.engine.events.on('clue', (clueId) => {
      const clue = this.engine.getAllChapterClues().find((c) => c.id === clueId);
      this.audio.play('clue_found');
      this.hud.toast('手がかりを手に入れた', clue?.title ?? clueId, 'clue');
    });
    this.engine.events.on('card', (cardId) => {
      const card = this.engine.getCard(cardId);
      this.audio.play('notify');
      this.hud.toast('知識カードを獲得', card?.term ?? cardId, 'card');
    });
    this.engine.events.on('description', ({ text, terms }) => {
      // 会話中に説明が来た場合は、会話が終わってから見せる
      if (this.dialogueWindow.isOpen) return;
      if (!this.inspect.isOpen) {
        this.pointerLock.release();
        this.inspect.show('', text, terms, []);
      }
    });
    this.engine.events.on('world-changed', () => this.refreshWorldVisuals());
    this.engine.events.on('hint', () => this.refreshHud());
  }

  // ------------------------------------------------------------
  // 設定
  // ------------------------------------------------------------

  private applySettings(settings: GameSettings, persist = true): void {
    this.settings = settings;
    this.scene.setFov(settings.fov);
    this.player.setSettings(settings);
    this.audio.setVolumes(settings.volumes);
    if (persist) persistSettings(settings);
  }

  dispose(): void {
    this.running = false;
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    this.pointerLock.dispose();
    this.touchControls.reset();
    this.audio.dispose();
    this.scene.dispose();
  }
}
